/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {environment} from '../../../environments/environment';
import {Injectable} from '@angular/core';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import {ClaimCommandModel} from './contracts/claim-command.model';
import {Observable} from 'rxjs';
import {ClaimBaseModel} from './contracts/claim-base.model';
import {ClaimResultModel} from './contracts/claim-result.model';
import {ClaimDetailModel} from './contracts/claim-detail.model';
import {ClaimReportResultModel} from './contracts/claim-report-result.model';
import {ClaimReportQueryModel} from './contracts/claim-report-query.model';
import {ClaimQueryModel} from './contracts/claim-query.model';
import {ClaimConfigModel} from './contracts/claim-config.model';
import {Cacheable} from '../../decorators/cacheable.decorator';
import {ClaimDashboardModel} from './contracts/claim-dashboard.model';

@Injectable({
    providedIn: 'root'
})
export class ClaimsApi {

    private claimsBaseUrl = `${environment.claimsBaseUrl}/api/claim`;
    private securityBaseUrl = `${environment.securityBaseUrl}/api/entityGroup`;
    private httpOptions = {
        headers: new HttpHeaders({
            'accept': 'application/json',
            'Content-Type': 'application/json',
        })
    };

    constructor(private http: HttpClient) {
    }

    @Cacheable()
    getClaimConfig(): Observable<ClaimConfigModel> {
        return this.http.get<ClaimConfigModel>(`${this.claimsBaseUrl}/config`);
    }

    createClaim(claim: ClaimCommandModel): Observable<ClaimBaseModel> {
        return this.http.post<ClaimBaseModel>(this.claimsBaseUrl, claim);
    }

    deleteClaim(number: string): Observable<void> {
        return this.http.delete<void>(`${this.claimsBaseUrl}/${number}`);
    }

    createAndSubmitClaim(claim: ClaimCommandModel): Observable<HttpResponse<any>> {
        // The observe option specifies how much of the response to return. The default value is 'body'.
        // In this case we must have access to the response status, so we must return the full response.
        return this.http.post<HttpResponse<any>>(`${this.claimsBaseUrl}/submit`, claim, {observe: 'response'});
    }

    submitSavedClaim(claimNumber: string): Observable<void> {
        return this.http.put<void>(`${this.claimsBaseUrl}/${claimNumber}/submit`, this.httpOptions);
    }

    getClaimDetail(claimNumber: string): Observable<ClaimDetailModel> {
        return this.http.get<ClaimDetailModel>(`${this.claimsBaseUrl}/detail/${claimNumber}`);
    }

    updateClaim(claimNumber: string, claim: ClaimCommandModel): Observable<ClaimBaseModel> {
        return this.http.put<ClaimBaseModel>(`${this.claimsBaseUrl}/${claimNumber}`, claim);
    }

    /**
     * Gets Claims from the BE
     * @param claimQuery the search query criteria
     */
    getClaims(claimQuery: ClaimQueryModel): Observable<ClaimResultModel> {
        return this.http.post<ClaimResultModel>(`${this.claimsBaseUrl}/search`, claimQuery);
    }

    getClaimBaseInfo(claimNumber: string): Observable<ClaimBaseModel> {
        return this.http.get<ClaimBaseModel>(`${this.claimsBaseUrl}/base/${claimNumber}`);
    }

    getClaimsReport(queryModel: ClaimReportQueryModel): Observable<ClaimReportResultModel> {
        return this.http.post<ClaimReportResultModel>(`${this.claimsBaseUrl}/report/search`, queryModel);
    }

    getClaimsDashboard(partnerCode: string): Observable<ClaimDashboardModel> {
        return this.http.get<ClaimDashboardModel>(`${this.claimsBaseUrl}/dashboard/`+partnerCode);
    }

    getPartnerCodes(): Observable<Map<string, string>> {
        return this.http.get<Map<string, string>>(`${this.securityBaseUrl}/dashboard/partnercodes`);
    }
}
